import { Button } from '@ksyos/design-system';
import type { ValidationErrors } from '../../../../../utils/validation';
import { pulmoCcqCustomControlDataSchema } from '../../../../../../common/validation/customControls/pulmonology';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../../../utils/validation';
import { FormDivider, ThirdHeader } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormContextProvider } from '../../../../elements/formContext';
import { Modal } from '../../../../elements/modals/Modal';
import { SteppedSliderField } from '../../../../fields/SteppedSlider';
import { AnamneseType } from './anamneseType';
import { AnamneseWrapper } from './AnamneseWrapper';

interface IAnamneseCcqProps {
    anamneseData: ICcq | null;
    deleteAnamneseData: () => Promise<void>;
    onClose: () => Promise<void>;
    setAnamneseData: (item: ICcq) => Promise<void>;
}

interface IAnamneseCcqState {
    anamneseData: ICcq;
    errors: ValidationErrors;
}

export interface ICcq {
    angstig_bezorgd_voor_de_volgende_benauwdheidsaanval: string;
    dagelijkse_activiteiten_u_zelf_aankleden_wassen: string;
    gehoest: string;
    kortademig_gedurende_lichamelijke_inspanning: string;
    kortademig_in_rust: string;
    matige_lichamelijke_activiteiten_wandelen_huishoudelijk_werk_boodschappen_doen: string;
    neerslachtig_vanwege_uw_ademhalingsproblemen: string;
    scoreCcqComplaints: number | null;
    scoreCcqCondition: number | null;
    scoreCcqEmotions: number | null;
    scoreCcqTotal: number | null;
    slijm_opgehoest: string;
    sociale_activiteiten_praten_omgaan_met_kinderen_vrienden_familie_bezoeken: string;
    zware_lichamelijke_activiteiten_trap_lopen_haasten_sporten: string;
}

const answersCcq = ['Nooit', 'Zelden', 'Af en toe', 'Regelmatig', 'Heel vaak', 'Meestal', 'Altijd'];
const answersCcqSecond = [
    'Helemaal niet beperkt',
    'Héél weinig beperkt',
    'Een beetje beperkt',
    'Tamelijk beperkt',
    'Erg beperkt',
    'Heel erg beperkt',
    'Volledig beperkt / of niet mogelijk',
];

const questionsCcq: { question: keyof ICcq; subCategory: string }[] = [
    { question: 'kortademig_in_rust', subCategory: 'complaints' },
    { question: 'kortademig_gedurende_lichamelijke_inspanning', subCategory: 'complaints' },
    { question: 'angstig_bezorgd_voor_de_volgende_benauwdheidsaanval', subCategory: 'emotions' },
    { question: 'neerslachtig_vanwege_uw_ademhalingsproblemen', subCategory: 'emotions' },
    { question: 'gehoest', subCategory: 'complaints' },
    { question: 'slijm_opgehoest', subCategory: 'complaints' },
    { question: 'zware_lichamelijke_activiteiten_trap_lopen_haasten_sporten', subCategory: 'condition' },
    {
        question: 'matige_lichamelijke_activiteiten_wandelen_huishoudelijk_werk_boodschappen_doen',
        subCategory: 'condition',
    },
    { question: 'dagelijkse_activiteiten_u_zelf_aankleden_wassen', subCategory: 'condition' },
    { question: 'sociale_activiteiten_praten_omgaan_met_kinderen_vrienden_familie_bezoeken', subCategory: 'condition' },
];

export class AnamneseCcq extends Component<IAnamneseCcqProps, IAnamneseCcqState> {
    constructor(props: IAnamneseCcqProps, context?: {}) {
        super(props, context);
        this.state = {
            anamneseData: {
                kortademig_in_rust: 'Regelmatig',
                kortademig_gedurende_lichamelijke_inspanning: 'Regelmatig',
                angstig_bezorgd_voor_de_volgende_benauwdheidsaanval: 'Regelmatig',
                neerslachtig_vanwege_uw_ademhalingsproblemen: 'Regelmatig',
                gehoest: 'Regelmatig',
                slijm_opgehoest: 'Regelmatig',
                zware_lichamelijke_activiteiten_trap_lopen_haasten_sporten: 'Tamelijk beperkt',
                matige_lichamelijke_activiteiten_wandelen_huishoudelijk_werk_boodschappen_doen: 'Tamelijk beperkt',
                dagelijkse_activiteiten_u_zelf_aankleden_wassen: 'Tamelijk beperkt',
                sociale_activiteiten_praten_omgaan_met_kinderen_vrienden_familie_bezoeken: 'Tamelijk beperkt',
                scoreCcqTotal: null,
                scoreCcqComplaints: null,
                scoreCcqEmotions: null,
                scoreCcqCondition: null,
                ...(this.props.anamneseData || {}),
            },
            errors: emptyValidationErrors,
        };
    }

    public render() {
        return (
            <FormContextProvider
                value={{
                    placeholderGroupName: 'request',
                    item: this.state.anamneseData,
                    errors: this.state.errors,
                    onChange: async (key, value) => await this.handleChange(key, value),
                    onDoValidate: async (key) => await this.validateOne(key),
                }}
            >
                <Modal
                    actionButtons={[
                        <Button
                            onClick={async () => await this.props.deleteAnamneseData()}
                            variant="outline"
                        >
                            Verwijder
                        </Button>,
                        <Button
                            onClick={async () => await this.validateAnamneseData()}
                            variant="solid"
                        >
                            Anamnese opslaan
                        </Button>,
                    ]}
                    isOpen
                    onClose={async () => await this.props.onClose()}
                    size="large"
                    title="CCQ anamnese"
                >
                    <AnamneseWrapper anamneseType={AnamneseType.CCQ}>
                        <ThirdHeader text="Hoe vaak voelde u zich in de afgelopen week..." />

                        <SteppedSliderField
                            fieldKey="kortademig_in_rust"
                            label="Kortademig in rust?"
                            options={answersCcq}
                            showLabels
                        />
                        <SteppedSliderField
                            fieldKey="kortademig_gedurende_lichamelijke_inspanning"
                            label="Kortademig gedurende lichamelijke inspanning?"
                            options={answersCcq}
                            showLabels
                        />
                        <SteppedSliderField
                            fieldKey="angstig_bezorgd_voor_de_volgende_benauwdheidsaanval"
                            label="Angstig/bezorgd voor de volgende benauwdheidsaanval?"
                            options={answersCcq}
                            showLabels
                        />
                        <SteppedSliderField
                            fieldKey="neerslachtig_vanwege_uw_ademhalingsproblemen"
                            label="Neerslachtig vanwege uw ademhalingsproblemen?"
                            options={answersCcq}
                            showLabels
                        />

                        <FormDivider />
                        <ThirdHeader text="In de afgelopen week, hoe vaak heeft u ..." />

                        <SteppedSliderField
                            fieldKey="gehoest"
                            label="Gehoest?"
                            options={answersCcq}
                            showLabels
                        />
                        <SteppedSliderField
                            fieldKey="slijm_opgehoest"
                            label="Slijm opgehoest?"
                            options={answersCcq}
                            showLabels
                        />

                        <FormDivider />
                        <ThirdHeader text="In welke mate voelde u zich in de afgelopen week beperkt door uw ademhalingsproblemen bij het uitvoeren van ..." />

                        <SteppedSliderField
                            fieldKey="zware_lichamelijke_activiteiten_trap_lopen_haasten_sporten"
                            label="Zware lichamelijke activiteiten (traplopen, haasten, sporten)?"
                            options={answersCcqSecond}
                            showLabels
                        />
                        <SteppedSliderField
                            fieldKey="matige_lichamelijke_activiteiten_wandelen_huishoudelijk_werk_boodschappen_doen"
                            label="Matige lichamelijke activiteiten (wandelen, huishoudelijk werk, boodschappen doen)?"
                            options={answersCcqSecond}
                            showLabels
                        />
                        <SteppedSliderField
                            fieldKey="dagelijkse_activiteiten_u_zelf_aankleden_wassen"
                            label="Dagelijkse activiteiten (jezelf aankleden, wassen)?"
                            options={answersCcqSecond}
                            showLabels
                        />
                        <SteppedSliderField
                            fieldKey="sociale_activiteiten_praten_omgaan_met_kinderen_vrienden_familie_bezoeken"
                            label="Sociale activiteiten (praten, omgaan met kinderen, vrienden/familie bezoeken)?"
                            options={answersCcqSecond}
                            showLabels
                        />
                    </AnamneseWrapper>
                </Modal>
            </FormContextProvider>
        );
    }

    private async handleChange<K extends keyof ICcq>(name: K, value: ICcq[K]) {
        await this.setState(({ anamneseData }) => ({ anamneseData: { ...anamneseData, [name]: value } }));
    }

    private async validateOne(name: keyof ICcq) {
        await this.setState(({ anamneseData, errors }) => ({
            errors: updateValidationErrorsForOne(pulmoCcqCustomControlDataSchema, anamneseData, errors, name),
        }));
    }

    private async validateAnamneseData() {
        const score = this.getCcqScores(this.state.anamneseData);
        await this.setState(({ anamneseData }) => ({
            anamneseData: {
                ...anamneseData,
                scoreCcqTotal: score.scoreCcqTotal,
                scoreCcqComplaints: score.scoreCcqComplaints,
                scoreCcqCondition: score.scoreCcqCondition,
                scoreCcqEmotions: score.scoreCcqEmotions,
            },
        }));
        const { hasErrors, errors, item } = validate(pulmoCcqCustomControlDataSchema, this.state.anamneseData);
        await this.setState({ errors });
        if (!hasErrors) {
            this.props.setAnamneseData(item as ICcq);
        }
    }

    private getCcqScores(anamneseData: ICcq) {
        let scoreCcqTotal = null;
        let scoreCcqComplaints = null;
        let scoreCcqEmotions = null;
        let scoreCcqCondition = null;
        if (anamneseData) {
            for (const question of questionsCcq) {
                let answer = answersCcq.findIndex((answerCcq) => answerCcq === anamneseData[question.question]);
                if (answer === -1) {
                    answer = answersCcqSecond.findIndex((answerCcq) => answerCcq === anamneseData[question.question]);
                }
                if (answer !== -1) {
                    scoreCcqTotal = scoreCcqTotal !== null ? scoreCcqTotal + answer : answer;
                    if (question.subCategory === 'complaints') {
                        scoreCcqComplaints = scoreCcqComplaints !== null ? scoreCcqComplaints + answer : answer;
                    } else if (question.subCategory === 'emotions') {
                        scoreCcqEmotions = scoreCcqEmotions !== null ? scoreCcqEmotions + answer : answer;
                    } else if (question.subCategory === 'condition') {
                        scoreCcqCondition = scoreCcqCondition !== null ? scoreCcqCondition + answer : answer;
                    }
                } else {
                    throw new Error('Answer not found');
                }
            }
        }

        return {
            scoreCcqTotal: Number((scoreCcqTotal! / questionsCcq.length).toFixed(2)),
            scoreCcqComplaints:
                scoreCcqComplaints !== null
                    ? Number(
                          (
                              scoreCcqComplaints /
                              questionsCcq.filter((question) => question.subCategory === 'complaints').length
                          ).toFixed(2)
                      )
                    : null,
            scoreCcqEmotions:
                scoreCcqEmotions !== null
                    ? Number(
                          (
                              scoreCcqEmotions /
                              questionsCcq.filter((question) => question.subCategory === 'emotions').length
                          ).toFixed(2)
                      )
                    : null,
            scoreCcqCondition:
                scoreCcqCondition !== null
                    ? Number(
                          (
                              scoreCcqCondition /
                              questionsCcq.filter((question) => question.subCategory === 'condition').length
                          ).toFixed(2)
                      )
                    : null,
        };
    }
}
